import React, { useState } from 'react';

export type AiInsightItem = {
  title: string;
  body: string;
};

export type AiInsightsData = {
  keyword: string;
  service: string;
  headline: string;
  summary: string;
  insights: AiInsightItem[];
  opportunities: string[];
  contentIdeas: string[];
  cautions: string[];
};

type Props = {
  data: AiInsightsData | null;
  cached: boolean;
  loading: boolean;
  error: string | null;
  onFetch: () => void;
  onForceFetch?: () => void;
};

const AiInsightsCard: React.FC<Props> = ({ data, cached, loading, error, onFetch, onForceFetch }) => {
  const [copied, setCopied] = useState(false);

  const buildCopyText = () => {
    if (!data) return '';

    return [
      `AIインサイト: ${data.keyword}`,
      '',
      data.headline,
      data.summary,
      '',
      '主要インサイト',
      ...data.insights.map((item) => `- ${item.title}: ${item.body}`),
      '',
      '狙えそうな切り口',
      ...data.opportunities.map((item) => `- ${item}`),
      '',
      'コンテンツ/施策案',
      ...data.contentIdeas.map((item) => `- ${item}`),
      '',
      '注意点',
      ...data.cautions.map((item) => `- ${item}`),
    ].filter(Boolean).join('\n');
  };

  const handleCopy = async () => {
    if (!data) return;

    try {
      await navigator.clipboard.writeText(buildCopyText());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (copyError) {
      console.error('AIインサイトのコピーに失敗:', copyError);
    }
  };

  return (
    <section className="bg-white shadow-sm rounded-lg p-6 min-w-0">
      <div className="flex flex-col gap-3 border-b pb-2 mb-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-bold text-gray-800">AIインサイト β</h2>
            {cached && (
              <span className="text-[11px] font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700">
                保存済みデータを表示中
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-gray-500">サジェスト・意図分類・ボリュームをもとに要約</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data && (
            <button
              type="button"
              onClick={handleCopy}
              className={`px-3 py-2 rounded text-sm font-semibold text-white ${
                copied ? 'bg-emerald-500' : 'bg-slate-600 hover:bg-slate-700'
              }`}
            >
              {copied ? 'コピー済み' : 'コピー'}
            </button>
          )}
          {data && onForceFetch && (
            <button
              type="button"
              onClick={onForceFetch}
              disabled={loading}
              className="rounded border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              再生成
            </button>
          )}
          <button
            type="button"
            onClick={() => onFetch()}
            disabled={loading}
            className="bg-blue-500 text-white px-3 py-2 rounded text-sm font-semibold hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? '生成中...' : 'インサイトを生成'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error}
        </div>
      )}

      {!data ? (
        <div className="h-[180px] flex items-center justify-center text-center text-sm text-gray-500">
          ボタンを押すとAIが分析結果を要約します
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded border border-blue-100 bg-blue-50 p-4">
            <p className="text-xs font-semibold text-blue-600">{data.keyword}</p>
            <h3 className="mt-1 text-xl font-bold text-gray-900">{data.headline}</h3>
            <p className="mt-2 text-sm leading-6 text-gray-700">{data.summary}</p>
          </div>

          {data.insights.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {data.insights.map((item) => (
                <div key={`${item.title}-${item.body}`} className="rounded border border-gray-100 p-4">
                  <h4 className="font-bold text-gray-800">{item.title}</h4>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{item.body}</p>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="rounded border border-gray-100 p-4">
              <h4 className="text-sm font-bold text-gray-800">狙えそうな切り口</h4>
              <ul className="mt-2 space-y-2 text-sm text-gray-600">
                {data.opportunities.map((item) => <li key={item}>・{item}</li>)}
              </ul>
            </div>
            <div className="rounded border border-gray-100 p-4">
              <h4 className="text-sm font-bold text-gray-800">コンテンツ/施策案</h4>
              <ul className="mt-2 space-y-2 text-sm text-gray-600">
                {data.contentIdeas.map((item) => <li key={item}>・{item}</li>)}
              </ul>
            </div>
            <div className="rounded border border-gray-100 p-4">
              <h4 className="text-sm font-bold text-gray-800">注意点</h4>
              <ul className="mt-2 space-y-2 text-sm text-gray-600">
                {data.cautions.map((item) => <li key={item}>・{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AiInsightsCard;
